import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { fetchApartmentFeeNoticeHtml } from '../../utils/apartmentFeeNoticePreview';

interface ApartmentFeeNoticePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  apartmentId: string;
  apartmentCode?: string;
  detailed?: boolean;
}

export const ApartmentFeeNoticePreviewModal: React.FC<ApartmentFeeNoticePreviewModalProps> = ({
  isOpen,
  onClose,
  apartmentId,
  apartmentCode,
  detailed = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [html, setHtml] = useState('');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!isOpen || !apartmentId) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setHtml('');

    fetchApartmentFeeNoticeHtml(apartmentId, { detailed })
      .then((content) => {
        if (!cancelled) {
          setHtml(content);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          const err = error as { response?: { data?: { message?: string } } };
          toast.error(err?.response?.data?.message || 'Không thể tải thông báo phí');
          onClose();
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, apartmentId, onClose, detailed]);

  if (!isOpen) {
    return null;
  }

  const titleBase = detailed ? 'Thông báo phí chi tiết' : 'Thông báo phí';
  const title = apartmentCode ? `${titleBase} - ${apartmentCode}` : titleBase;

  const handlePrint = () => {
    const frame = iframeRef.current;
    const win = frame?.contentWindow;
    if (!win || !html) {
      toast.error('Không thể in thông báo phí');
      return;
    }
    try {
      win.focus();
      win.print();
    } catch {
      toast.error('Trình duyệt chặn in. Vui lòng dùng Ctrl+P trong khung xem trước.');
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between border-b px-6 py-4 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        </div>

        <div className="flex-1 min-h-0 p-4 bg-gray-100">
          {loading ? (
            <div className="flex justify-center items-center py-24">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
              <span className="ml-2 text-gray-600">Đang tải thông báo phí...</span>
            </div>
          ) : html ? (
            <iframe
              ref={iframeRef}
              title={title}
              srcDoc={html}
              className="w-full h-full min-h-[70vh] border-0 bg-white rounded"
              sandbox="allow-same-origin allow-scripts allow-modals"
            />
          ) : (
            <div className="text-center py-24 text-gray-500">Không có nội dung</div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t px-6 py-4 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
          >
            Đóng
          </button>
          <button
            type="button"
            disabled={!html || loading}
            onClick={handlePrint}
            className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded disabled:opacity-50"
          >
            In thông báo phí
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApartmentFeeNoticePreviewModal;
